import mapValues from 'lodash/mapValues'
import { TAG_TYPE_ACCOUNT, TAG_TYPE_LOCATION, TAG_TYPE_SERVICE } from 'slices/tags/tagsSlice'
import { get } from 'utils/lodash'

const getSafeArray = value => (Array.isArray(value) ? value : [])

/**
 * Returns the subset of `savedTagsGroup.tags` whose ids are present in `allActiveTags`.
 * Used to seed the editor's visible selection with active-only options.
 *
 * Mirrors the previous inline behavior of EditTagsPopup so existing UI logic is unchanged.
 */
export const getActiveSelectedTags = (allActiveTags, savedTagsGroup = {}) => {
  const { parentId, tags = [] } = savedTagsGroup
  const allActiveTagsSafe = getSafeArray(allActiveTags)
  const allSelectedIds = getSafeArray(tags).map(({ id }) => id)

  return {
    parentId,
    tags: allActiveTagsSafe.filter(({ id }) => allSelectedIds.includes(id)),
  }
}

/**
 * Returns the ids of saved tags that are still assigned to the entity but have
 * been deactivated in Settings (`active === false`). The editor cannot show
 * them, so we hold onto them and merge them back into the save payload to
 * avoid silently dropping them (CS-2624).
 *
 * Tags whose `for_*` flag does not match the section (e.g. a `for_service=true`
 * tag attached to an account rowid) are NOT preserved here: those are data
 * orphans and re-submitting their id on save makes the chip un-removable
 * (CS-2764). Letting them drop out of `selectedTagIds` lets the backend diff
 * clean them up on the next save.
 */
export const getPreservedInactiveTagIds = (allActiveTags, savedTagsGroup = {}) => {
  const { tags = [] } = savedTagsGroup
  const activeIdSet = new Set(getSafeArray(allActiveTags).map(({ id }) => id))

  return getSafeArray(tags)
    .filter(tag => tag && tag.active === false && !activeIdSet.has(tag.id))
    .map(({ id }) => id)
}

/**
 * Convenience helper that builds the per-tag-type map of preserved inactive ids
 * from the popup's resolved active metadata and the incoming defaultSelectedTags
 * prop.
 */
export const buildPreservedInactiveTagIdsByType = ({
  allAccountTags,
  allLocationTags,
  allServiceTags,
  defaultSelectedTags = {},
}) => ({
  [TAG_TYPE_ACCOUNT]: getPreservedInactiveTagIds(allAccountTags, get(defaultSelectedTags, [TAG_TYPE_ACCOUNT], {})),
  [TAG_TYPE_LOCATION]: getPreservedInactiveTagIds(allLocationTags, get(defaultSelectedTags, [TAG_TYPE_LOCATION], {})),
  [TAG_TYPE_SERVICE]: getPreservedInactiveTagIds(allServiceTags, get(defaultSelectedTags, [TAG_TYPE_SERVICE], {})),
})

/**
 * Merges the preserved inactive tag ids back into the save payload, per tag type.
 *
 * - Only tag types present in `selectionsByType` are touched, so disabled tag
 *   types stay out of the request body.
 * - Existing ids are not duplicated.
 * - The shape `{ parentId, selectedTagIds }` matches what the save endpoint
 *   already expects from the Redux selection state.
 */
export const mergePreservedInactiveIds = (selectionsByType = {}, preservedInactiveIdsByType = {}) =>
  mapValues(selectionsByType, (group, tagType) => {
    const preserved = getSafeArray(preservedInactiveIdsByType[tagType])
    if (preserved.length === 0) return group

    const currentIds = getSafeArray(group?.selectedTagIds)
    const currentIdSet = new Set(currentIds)
    const idsToAppend = preserved.filter(id => !currentIdSet.has(id))

    if (idsToAppend.length === 0) return group

    return {
      ...group,
      selectedTagIds: [...currentIds, ...idsToAppend],
    }
  })
